import { useEffect, useRef } from "react";
import { useCarried } from "./useCarried";

export interface StickyOptions {
  /** A selector for the scrolling container of the element. Defaults to the first container with an `overflowY` of `auto` or `scroll`. */
  scrollContainerSelector?: string | ((element: HTMLElement) => boolean);
  /** A selector for the element's limiting container. Defaults to the first relatively or absolutely positioned parent, or the scroll container.
   * Thus, to create a limiting box, give some parent of the element relative or absolute positioning.
   * Do not override this unless you know what you are doing. */
  limitsContainerSelector?: string | ((element: HTMLElement) => boolean);

  /** The horizontal side the element will stick to, if any. */
  xStick?: "none" | "left" | "right";
  /** The vertical side the element will stick to, if any. */
  yStick?: "none" | "top" | "bottom";

  /** Distance from the top of the limits container from which the element will float. */
  top?: number;
  /** Distance from the bottom of the limits container from which the element will float. */
  bottom?: number;
  /** Distance from the left of the limits container from which the element will float. */
  left?: number;
  /** Distance from the right of the limits container from which the element will float. */
  right?: number;

  /** Pixels from the top of the scroll window at which the element will stick. */
  topStick?: number;
  /** Pixels from the bottom of the scroll window at which the element will stick. */
  bottomStick?: number;
  /** Pixels from the left of the scroll window at which the element will stick. */
  leftStick?: number;
  /** Pixels from the right of the scroll window at which the element will stick. */
  rightStick?: number;
}

/** Select the first parent of `element` matching the `selector` css selector string or function. */
const selectParent = (
  element: HTMLElement,
  selector: string | ((element: HTMLElement) => boolean)
) => {
  if (typeof selector === "string") return element.closest(selector);
  let node = element.parentElement;
  while (node) {
    if (selector(node)) return node;
    node = node.parentElement;
  }
  return null;
};

/** Convert a numeric/string value to pixels. */
const px = (value: number | string | undefined) =>
  value == null || value === "" ? "" : value + "px";

/** Callback to add sticky management to element with options. Returns a function for removing sticky management.
 * Add a relative
 */
export const stickyManager = (element: HTMLElement, options: StickyOptions) => {
  const scroller = selectParent(
    element,
    options.scrollContainerSelector ??
      ((node) => ["auto", "scroll"].includes(getComputedStyle(node).overflowY))
  );
  let limiter = selectParent(
    element,
    (node) =>
      node === scroller ||
      ["relative", "absolute"].includes(getComputedStyle(node).position)
  );

  if (!scroller) return;
  if (limiter === scroller) limiter = null;

  // scroll handler
  const onScroll = () => {
    const {
      xStick = "none",
      yStick = "none",
      left = 0,
      right = 0,
      top = 0,
      bottom = 0,
      leftStick = 0,
      rightStick = 0,
      topStick = 0,
      bottomStick = 0,
    } = options;

    // reset element style to capture true position
    element.style.left =
      xStick === "none" ? px(options.left) : xStick === "left" ? px(left) : "";
    element.style.right =
      xStick === "none"
        ? px(options.right)
        : xStick === "right"
          ? px(right)
          : "";
    element.style.top =
      yStick === "none" ? px(options.top) : yStick === "top" ? px(top) : "";
    element.style.bottom =
      yStick === "none"
        ? px(options.bottom)
        : yStick === "bottom"
          ? px(bottom)
          : "";
    element.style.position = "absolute";

    // get sticky distance, and bounding boxes
    const scrollBox = scroller.getBoundingClientRect();
    const limitBox = limiter?.getBoundingClientRect() ?? scrollBox;
    const elementBox = element.getBoundingClientRect();

    // handle x-stick
    if (xStick !== "none") {
      if (
        xStick === "left"
          ? scrollBox.left + elementBox.width + leftStick + right >
            limitBox.right
          : scrollBox.right - elementBox.width - rightStick - left <
            limitBox.left
      ) {
        // element is surpassing limits box: stop sticking at limits box edge
        if (xStick === "left") {
          element.style.left = "auto";
          element.style.right = px(right);
        } else {
          element.style.right = "auto";
          element.style.left = px(left);
        }
      } else if (
        xStick === "left"
          ? elementBox.left - leftStick < scrollBox.left
          : elementBox.right + rightStick > scrollBox.right
      ) {
        // element is cut off by scroll box: stick to scroll box edge
        element.style.position = "fixed";
        if (xStick === "left") {
          element.style.right = "auto";
          element.style.left = px(scrollBox.left + leftStick);
        } else {
          element.style.left = "auto";
          element.style.right = px(innerWidth - scrollBox.right - rightStick);
        }
      }
    }

    // handle y-stick
    if (yStick !== "none") {
      if (
        yStick === "top"
          ? scrollBox.top + elementBox.height + topStick + bottom >
            limitBox.bottom
          : scrollBox.bottom - elementBox.height - bottomStick - top <
            limitBox.top
      ) {
        // element is surpassing limits box: stop sticking at limits box edge
        if (yStick === "top") {
          element.style.top = "auto";
          element.style.bottom = px(bottom);
        } else {
          element.style.bottom = "auto";
          element.style.top = px(top);
        }
      } else if (
        yStick === "top"
          ? elementBox.top - topStick < scrollBox.top
          : elementBox.bottom + bottomStick > scrollBox.bottom
      ) {
        // element is cut off by scroll box: stick to scroll box edge
        element.style.position = "fixed";
        if (yStick === "top") {
          element.style.bottom = "auto";
          element.style.top = px(scrollBox.top + topStick);
        } else {
          element.style.top = "auto";
          element.style.bottom = px(
            innerHeight - scrollBox.bottom + bottomStick
          );
        }
      }
    }

    if (element.style.position === "fixed") {
      if (xStick === "none") {
        element.style.left = px(elementBox.left);
      }
      if (yStick === "none") {
        element.style.top = px(elementBox.top);
      }
    }
  };
  scroller.addEventListener("scroll", onScroll);
  window.addEventListener("resize", onScroll);

  onScroll();

  return () => {
    scroller.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", onScroll);
  };
};

/** React hook for adding sticky management to an element. Returns an object with stickyRef to be used as the sticky element's ref. */
export const useSticky = (options?: StickyOptions) => {
  const stickyRef = useRef(null);
  options = useCarried(options ?? {});

  useEffect(() => {
    if (!stickyRef.current) return;

    return stickyManager(stickyRef.current, options);
  }, [options]);

  return { stickyRef };
};
